import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ROUTES } from "@routing/routes";
import { hasNoLocationMembershipAtom } from "@/store/shipToAtoms";
import type { ShoppingCartItem } from "@models/shoppingCart";
import {
    currencyFormatter,
    formatPiecePricingFromCount,
    OUT_OF_STOCK,
} from "@utils/formatter";
import { PRODUCT_PLACEHOLDER_SRC } from "@utils/productImage";
import { NO_LOCATION_LINE_MESSAGE } from "@customer/_helpers/noLocationCopy";
import { lineTotal } from "../_helpers/cartTotals";
import { CartItemStepper } from "./CartItemStepper";

interface CartItemRowProps {
    item: ShoppingCartItem;
    imageUrl?: string;
    // The line's product read failed and holds nothing (B-04): the image, the piece pricing
    // and the stock hint are all missing for that reason, so the row says so.
    detailsFailed?: boolean;
    // Reads the failed product again (PR 356 review): the line's Retry.
    onRetryDetails?: () => void;
    // How many pieces one unit holds, from the product read (linePiecesPerUnit); undefined
    // while unknown. The piece price is derived here from the line's own price (B-03).
    piecesPerUnit?: number;
    onQuantityChange: (next: number) => void;
    onRemove: () => void;
    showRemove?: boolean;
    // The stepper's ceiling — the line's vendor stock where the caller knows it (#443).
    maxQuantity?: number;
    // No live vendor offers the line's sku (PO ruling 2026-11-02, lineHasNoOffers): it
    // can't be ordered, and the row says so, quotes no price and locks the quantity.
    outOfStock?: boolean;
    // Line-level copy the caller owns, rendered under the restriction line (#443's stock
    // notice and vendor switch, on the Cart page).
    notice?: ReactNode;
    // Why the server refused this line's last quantity change (#443: the quantity
    // update's 400 is said here, not toasted). Both pages that edit quantities pass it.
    quantityRefusal?: string;
}

export const CartItemRow = ({
    item,
    imageUrl,
    detailsFailed = false,
    onRetryDetails,
    piecesPerUnit,
    onQuantityChange,
    onRemove,
    showRemove = true,
    maxQuantity,
    outOfStock = false,
    notice,
    quantityRefusal,
}: CartItemRowProps) => {
    // #417. The payload says only THAT a line is restricted, never why, so a
    // member seated on no location is recognised by the shared atom — read
    // here, not passed in, so no caller can forget it.
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);
    // A piece is priced from THIS line's vendor price; nothing is shown for a
    // single-piece unit or an unknown count, as on the product page (B-03).
    const pricing = formatPiecePricingFromCount(item.price, piecesPerUnit);
    // The "Out of stock" mark's id: the quantity controls it disables point at it.
    const outOfStockId = useId();

    return (
        <div
            data-testid="cart-item-row"
            className="border-t border-base-300 py-4 flex gap-3 md:gap-4"
        >
            {/* TODO(backend): /shoppingcarts items have no image URL — Cart
                hydrates by fetching /products/{id}; backend should embed
                image URLs on the cart payload eventually. */}
            <div className="w-24 h-24 md:w-28 md:h-28 shrink-0 self-start rounded-md bg-base-100 overflow-hidden flex items-center justify-center">
                <img
                    src={imageUrl || PRODUCT_PLACEHOLDER_SRC}
                    alt={item.name}
                    className="w-full h-full object-contain"
                />
            </div>

            <div className="flex-1 min-w-0 flex flex-col gap-1">
                <Link
                    to={ROUTES.PRODUCT_DETAIL(item.productId)}
                    className="font-bold text-neutral hover:underline line-clamp-2"
                >
                    {item.name}
                </Link>

                {detailsFailed && (
                    <p
                        data-testid="cart-item-details-failed"
                        className="text-xs text-error"
                    >
                        Couldn&apos;t load this product&apos;s image and details.
                        {onRetryDetails && (
                            <button
                                type="button"
                                onClick={onRetryDetails}
                                className="link link-primary ml-1"
                            >
                                Retry
                            </button>
                        )}
                    </p>
                )}

                {/* A line no live vendor offers quotes no price: its price is the
                    dead offer's, and the cart's totals leave it out (offeredLines). */}
                {!outOfStock && (
                    <p className="text-sm text-neutral">
                        <span className="font-semibold">{pricing.unitPrice}</span>
                        <span className="text-neutral/70 ml-1">unit</span>
                        {pricing.showPiece && (
                            <>
                                <span className="text-neutral/40 mx-1">/</span>
                                <span className="font-semibold">
                                    {pricing.piecePrice}
                                </span>
                                <span className="text-neutral/70 ml-1">piece</span>
                            </>
                        )}
                    </p>
                )}

                {!outOfStock && pricing.showPiece && (
                    <p className="text-xs text-neutral/70">
                        {pricing.piecesPerUnit} pieces per unit
                    </p>
                )}

                {item.variant && (
                    <p className="text-xs text-neutral/70">
                        {item.variant.name}
                    </p>
                )}

                {/* #228 / E4-S12. Per-line, because the cart-level banner says how many but
                    not WHICH — and Remove is a per-line control, so the shopper has to be
                    able to see which line to remove. */}
                {/* #417. For a member on no location the flag is information,
                    not an error (see noLocationCopy.ts): info colour, and no
                    live role — the page-level note is the one announcement,
                    not one alert per line. */}
                {item.isRestricted &&
                    (hasNoLocationMembership ? (
                        <p
                            data-testid="cart-item-restricted"
                            className="text-sm font-semibold text-info"
                        >
                            {NO_LOCATION_LINE_MESSAGE}
                        </p>
                    ) : (
                        <p
                            role="alert"
                            data-testid="cart-item-restricted"
                            className="text-sm font-semibold text-error"
                        >
                            Can&apos;t ship to your selected location
                        </p>
                    ))}

                {/* No live role: the page states the reason once (the Cart
                    summary's out-of-stock reason, Checkout's Place Order
                    reason), not one alert per line, as #417 does for the
                    no-location flag. The line's quantity controls point at it. */}
                {outOfStock && (
                    <p
                        id={outOfStockId}
                        data-testid="cart-item-out-of-stock"
                        className="text-sm font-semibold text-error"
                    >
                        {OUT_OF_STOCK}
                    </p>
                )}

                {notice}

                {quantityRefusal && (
                    <p
                        role="alert"
                        data-testid="cart-item-quantity-refusal"
                        className="text-sm font-semibold text-error"
                    >
                        {quantityRefusal}
                    </p>
                )}

                <div className="flex items-center justify-between gap-3 mt-2">
                    <div className="flex-1 max-w-[14rem]">
                        <CartItemStepper
                            value={item.quantity}
                            onChange={onQuantityChange}
                            onRemove={onRemove}
                            max={maxQuantity}
                            showRemove={showRemove}
                            // The API refuses every quantity change on the line;
                            // Remove is the way out.
                            disabled={outOfStock}
                            describedBy={outOfStock ? outOfStockId : undefined}
                        />
                    </div>
                    {!outOfStock && (
                        <p className="font-bold text-neutral">
                            {currencyFormatter.format(lineTotal(item))}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
